import { Spacer } from "@hackthenorth/north";
import {
  ArrowLeftIcon,
  MinusSmallIcon,
  PlusSmallIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";
import { Button, DropdownMenu, Text, TextInput } from "north.js";
import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";

import { PageWrapper } from "src/shared/components";

import "twin.macro";

import { BaseRoute, MentorRoute } from "src/shared/constants/route";
import {
  getVolunteerShiftDayOfWeek,
  getVolunteerShiftHourAndMinute24Hour,
} from "src/views/volunteer/shared/utils/date";

import { useEditMentorShiftContext } from "../../shared/contexts/EditMentorShiftContext";
import { useDeleteMentorShiftMutation } from "../graphql/deleteMentorShift.generated";
import { useMentorShiftsLazyQuery } from "../graphql/mentorShifts.generated";

const EditMentorShiftPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const shiftId: number = useMemo(() => {
    return parseInt(searchParams.get("shift_id") ?? "");
  }, [searchParams]);

  const {
    shiftName,
    shiftDateStart,
    shiftDateEnd,
    timeStart,
    timeEnd,
    location,
    requiredMentors,

    updateShiftName,
    updateShiftDateStart,
    updateShiftDateEnd,
    updateTimeStart,
    updateTimeEnd,
    updateLocation,
    updateRequiredMentors,

    updateShift,
  } = useEditMentorShiftContext();

  const [getMentorShift, mentorShiftResult] = useMentorShiftsLazyQuery();

  const [deleteShift] = useDeleteMentorShiftMutation();

  // FETCH THE MENTOR SHIFT DATA
  useEffect(() => {
    if (shiftId) {
      getMentorShift({
        variables: {
          where: {
            id: {
              equals: shiftId,
            },
          },
        },
      });
    }
  }, [shiftId, getMentorShift]);

  const mentorShiftData = useMemo(() => {
    if (mentorShiftResult.data) {
      return mentorShiftResult.data.mentorShifts[0];
    }

    return null;
  }, [mentorShiftResult.data]);

  // SET INITIAL DATA
  useEffect(() => {
    if (mentorShiftData) {
      updateShiftName(mentorShiftData.name);
      updateShiftDateStart(
        getVolunteerShiftDayOfWeek(new Date(mentorShiftData.start_time))
      );
      updateShiftDateEnd(
        getVolunteerShiftDayOfWeek(new Date(mentorShiftData.end_time))
      );
      updateTimeStart(
        getVolunteerShiftHourAndMinute24Hour(
          new Date(mentorShiftData.start_time)
        )
      );
      updateTimeEnd(
        getVolunteerShiftHourAndMinute24Hour(new Date(mentorShiftData.end_time))
      );
      updateLocation(mentorShiftData.location ?? "");
    }
  }, [
    mentorShiftData,
    updateShiftName,
    updateShiftDateStart,
    updateShiftDateEnd,
    updateTimeStart,
    updateTimeEnd,
    updateLocation,
    updateRequiredMentors,
  ]);

  // Date selection choices
  const dateChoices = useMemo(() => {
    return (isStart: boolean) => [
      [
        {
          content: "Friday",
          action: (e: any) => {
            e.preventDefault();
            isStart
              ? updateShiftDateStart("Friday")
              : updateShiftDateEnd("Friday");
          },
        },
        {
          content: "Saturday",
          action: (e: any) => {
            e.preventDefault();
            isStart
              ? updateShiftDateStart("Saturday")
              : updateShiftDateEnd("Saturday");
          },
        },
        {
          content: "Sunday",
          action: (e: any) => {
            e.preventDefault();
            isStart
              ? updateShiftDateStart("Sunday")
              : updateShiftDateEnd("Sunday");
          },
        },
      ],
    ];
  }, [updateShiftDateStart, updateShiftDateEnd]);

  // Time Start and Time End selection choices
  const timeStartChoices = useMemo(() => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;
        times.push({
          content: time,
          action: (e: any) => {
            e.preventDefault();
            if (moment(time, "HH:mm").isBefore(moment(timeEnd, "HH:mm"))) {
              updateTimeStart(time);
            }
          },
        });
      }
    }
    return [times];
  }, [updateTimeStart, timeEnd]);

  const timeEndChoices = useMemo(() => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;
        times.push({
          content: time,
          action: (e: any) => {
            e.preventDefault();

            if (moment(time, "HH:mm").isAfter(moment(timeStart, "HH:mm")))
              updateTimeEnd(time);
          },
        });
      }
    }
    return [times];
  }, [updateTimeEnd, timeStart]);

  // Editing the shift
  const handleEditItem = useCallback(() => {
    updateShift(shiftId).then(() =>
      navigate(`${BaseRoute.MENTOR}/${MentorRoute.SHIFTS}`)
    );
  }, [updateShift, shiftId, navigate]);

  // Deleting the shift
  const handleDeleteShift = useCallback(async () => {
    try {
      await deleteShift({
        variables: {
          id: shiftId,
        },
      });

      navigate(`${BaseRoute.MENTOR}/${MentorRoute.SHIFTS}`);
    } catch (err) {
      console.error(err);
    }
  }, [deleteShift, navigate, shiftId]);

  return (
    <PageWrapper>
      <EditMentorShiftContainer>
        <div tw="flex flex-row gap-4 items-center">
          <ArrowLeftIcon tw="h-16 font-bold text-text-secondary cursor-pointer" />
          <Text
            type="button"
            tw="font-bold text-text-secondary cursor-pointer"
            color="secondary"
            onClick={() =>
              navigate(`${BaseRoute.MENTOR}/${MentorRoute.SHIFTS}`)
            }
          >
            Back to Mentor Shifts
          </Text>
        </div>

        <Spacer height={24} />

        <Text type="h2">Edit a Mentor shift</Text>
        <Spacer height={8} />

        <Text type="body1">
          Edit, edit, or delete shifts by filling the form below.
        </Text>
        <Spacer height={24} />

        {/* FORM */}
        <StyledForm>
          <InputContainer>
            <Text type="body1" tw="font-bold">
              Shift name
            </Text>

            <TextInput
              type="text"
              placeholder="Enter a name..."
              tw="text-text-tertiary font-medium h-48"
              width={"700px"}
              onChange={(e) => updateShiftName(e.target.value)}
              value={shiftName}
            />
          </InputContainer>

          <div tw="flex flex-row w-full gap-48">
            <InputContainer tw="flex-1">
              <Text type="body1" tw="font-bold">
                Date Start
              </Text>

              <StyledDropdown tw="w-full! z-30" items={dateChoices(true)}>
                <Button color="secondary" tw="w-full! h-48">
                  <div tw="flex justify-between w-full">
                    <Text type="body1" tw="text-text-tertiary font-medium">
                      {shiftDateStart === "" ? "Enter date" : shiftDateStart}
                    </Text>
                    <ChevronDown />
                  </div>
                </Button>
              </StyledDropdown>
            </InputContainer>

            <InputContainer tw="flex-1">
              <Text type="body1" tw="font-bold">
                Date End
              </Text>

              <StyledDropdown tw="w-full! z-30" items={dateChoices(false)}>
                <Button color="secondary" tw="w-full! h-48">
                  <div tw="flex justify-between w-full">
                    <Text type="body1" tw="text-text-tertiary font-medium">
                      {shiftDateEnd === "" ? "Enter date" : shiftDateEnd}
                    </Text>
                    <ChevronDown />
                  </div>
                </Button>
              </StyledDropdown>
            </InputContainer>
          </div>

          <div tw="flex flex-row w-full gap-48 z-20">
            <InputContainer tw="flex-1">
              <Text type="body1" tw="font-bold">
                Time Start
              </Text>

              <DropdownMenu
                tw="w-full! max-h-196 overflow-auto"
                items={timeStartChoices}
              >
                <Button color="secondary" tw="w-full! h-48">
                  <div tw="flex justify-between w-full">
                    <Text type="body1" tw="text-text-tertiary font-medium">
                      {timeStart === "" ? "Enter time start" : timeStart}
                    </Text>
                    <ChevronDown />
                  </div>
                </Button>
              </DropdownMenu>
            </InputContainer>

            <InputContainer tw="flex-1">
              <Text type="body1" tw="font-bold">
                Time End
              </Text>

              <DropdownMenu
                tw="w-full! max-h-196 overflow-auto"
                items={timeEndChoices}
              >
                <Button color="secondary" tw="w-full! h-48">
                  <div tw="flex justify-between w-full">
                    <Text type="body1" tw="text-text-tertiary font-medium">
                      {timeEnd === "" ? "Enter time end" : timeEnd}
                    </Text>
                    <ChevronDown />
                  </div>
                </Button>
              </DropdownMenu>
            </InputContainer>
          </div>

          <InputContainer>
            <Text type="body1" tw="font-bold">
              Location
            </Text>

            <TextInput
              type="text"
              placeholder="Enter location"
              tw="text-text-tertiary font-medium h-48 w-full!"
              width={"700px"}
              value={location}
              onChange={(e) => updateLocation(e.target.value)}
            />
          </InputContainer>

          <div tw="flex flex-row w-full gap-48">
            <InputContainer>
              <Text type="body1" tw="font-bold">
                Required Mentors
              </Text>

              <div tw="flex flex-row gap-12 items-center">
                <MinusSmallIcon
                  tw="cursor-pointer"
                  onClick={() => {
                    if (requiredMentors > 0)
                      updateRequiredMentors(requiredMentors - 1);
                  }}
                  height={24}
                />
                <div style={{ width: "48px" }}>
                  <TextInput
                    tw="text-text-secondary font-medium text-center h-48 w-48"
                    value={requiredMentors}
                  />
                </div>

                <PlusSmallIcon
                  tw="cursor-pointer"
                  onClick={() => {
                    updateRequiredMentors(requiredMentors + 1);
                  }}
                  height={24}
                />
              </div>
            </InputContainer>
          </div>
        </StyledForm>

        <Spacer height={64} />

        <div tw="flex flex-row gap-16">
          <Button color="secondary-danger">
            <Text type="button" onClick={handleDeleteShift}>
              Delete Shift
            </Text>
          </Button>

          <Button color="primary">
            <Text type="button" onClick={handleEditItem}>
              Save Item
            </Text>
          </Button>
        </div>
      </EditMentorShiftContainer>
    </PageWrapper>
  );
};

const ChevronDown = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-6 h-6"
    tw="text-text-tertiary w-12"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 8.25l-7.5 7.5-7.5-7.5"
    />
  </svg>
);

const EditMentorShiftContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 36px;
  width: 100%;
  max-width: 700px;
`;

const StyledDropdown = styled(DropdownMenu)`
  z-index: 1;
`;

export default EditMentorShiftPage;
